
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {LogoutReason} from './LogoutReason';
import {FieldModelLogoutReason} from './LogoutReason';

/**
 * LogoutNotify struct
 */
class LogoutNotify {
  /**
   * Initialize struct
   * @param {!string=} message
   * @param {!LogoutReason=} reason
   * @constructor
   */
  constructor (message = '', reason = new LogoutReason()) {
    this.Message = message
    this.Reason = reason
  }

  /**
   * Copy struct (shallow copy)
   * @this {!LogoutNotify}
   * @param {!LogoutNotify} other Other struct
   * @returns {!LogoutNotify} This struct
   */
  copy (other) {
    if (other.Message != null) {
      this.Message = other.Message
    } else {
      this.Message = null
    }
    if (other.Reason != null) {
      this.Reason = LogoutReason.fromObject(other.Reason)
    } else {
      this.Reason = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!LogoutNotify}
   * @returns {!LogoutNotify} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new LogoutNotifyModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new LogoutNotifyModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!LogoutNotify}
   * @param {!LogoutNotify} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof LogoutNotify)) {
      throw new TypeError('Instance of LogoutNotify is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!LogoutNotify}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Message: ((this.Message != null) ? this.Message : null),
      Reason: ((this.Reason != null) ? this.Reason : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return LogoutNotify.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!LogoutNotify} other Object value
   * @returns {!LogoutNotify} Created struct
   */
  static fromObject (other) {
    return new LogoutNotify().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!LogoutNotify}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return LogoutNotify.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!LogoutNotify}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5008
  }
}

export { LogoutNotify };

/**
 * Fast Binary Encoding LogoutNotify field model
 */
class FieldModelLogoutNotify extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Message = new fbe.FieldModelString(buffer, 4 + 4)
    this.Reason = new FieldModelLogoutReason(buffer, this.Message.fbeOffset + this.Message.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelLogoutNotify}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelLogoutNotify}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Message.fbeSize + this.Reason.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelLogoutNotify}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Message.fbeExtra + this.Reason.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelLogoutNotify}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelLogoutNotify.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelLogoutNotify}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5008
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelLogoutNotify}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelLogoutNotify}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Message.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Message.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Message.fbeSize

    if ((fbeCurrentSize + this.Reason.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Reason.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Reason.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelLogoutNotify}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelLogoutNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelLogoutNotify}
   * @param {!LogoutNotify} fbeValue Default value, defaults is new LogoutNotify()
   * @returns {!LogoutNotify} LogoutNotify value
   */
  get (fbeValue = new LogoutNotify()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelLogoutNotify}
   * @param {!LogoutNotify} fbeValue LogoutNotify value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Message.fbeSize) <= fbeStructSize) {
      fbeValue.Message = this.Message.get()
    } else {
      fbeValue.Message = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Message.fbeSize

    if ((fbeCurrentSize + this.Reason.fbeSize) <= fbeStructSize) {
      fbeValue.Reason = this.Reason.get()
    } else {
      fbeValue.Reason = new LogoutReason()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Reason.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelLogoutNotify}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelLogoutNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelLogoutNotify}
   * @param {!LogoutNotify} fbeValue LogoutNotify value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelLogoutNotify}
   * @param {LogoutNotify} fbeValue LogoutNotify value
   */
  setFields (fbeValue) {
    this.Message.set(fbeValue.Message)
    this.Reason.set(fbeValue.Reason)
  }
}

export { FieldModelLogoutNotify };

/**
 * Fast Binary Encoding LogoutNotify model
 */
class LogoutNotifyModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelLogoutNotify(this.buffer, 4)
  }

  /**
   * Get the LogoutNotify model
   * @this {!LogoutNotifyModel}
   * @returns {!FieldModelLogoutNotify} model LogoutNotify model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!LogoutNotifyModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!LogoutNotifyModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return LogoutNotifyModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!LogoutNotifyModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelLogoutNotify.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!LogoutNotifyModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!LogoutNotifyModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!LogoutNotifyModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!LogoutNotifyModel}
   * @param {!LogoutNotify} value LogoutNotify value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!LogoutNotifyModel}
   * @param {!LogoutNotify} value LogoutNotify value, defaults is new LogoutNotify()
   * @return {!object} Deserialized LogoutNotify value and its size
   */
  deserialize (value = new LogoutNotify()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new LogoutNotify(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new LogoutNotify(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!LogoutNotifyModel}
   * @param {!number} prev Previous LogoutNotify model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { LogoutNotifyModel };
